/**
 * User Profile Service
 *
 * Tech settings stores profile info (including photoUrl) at the
 * sub-collection `users/{userId}/settings/preferences`. This service
 * exposes a simple helper for fetching photos by user ID with an
 * in-memory cache so the same photo isn't re-downloaded per render.
 *
 * Photos are base64 data URLs uploaded from the tech settings page.
 * That's fine for our team size (10-20 people).
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase';

// Simple in-memory cache — process-lifetime.
// Null values are cached too (so we don't re-fetch for users without photos).
const photoCache = new Map<string, string | null>();

/**
 * Get a single user's photo URL. Returns null if no photo has been set.
 */
export async function getUserPhotoUrl(userId: string): Promise<string | null> {
  if (!userId) return null;
  if (photoCache.has(userId)) return photoCache.get(userId) ?? null;

  try {
    const ref = doc(db, 'users', userId, 'settings', 'preferences');
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      photoCache.set(userId, null);
      return null;
    }
    const data = snap.data() as { profile?: { photoUrl?: string } };
    const url = data?.profile?.photoUrl || null;
    photoCache.set(userId, url);
    return url;
  } catch (err) {
    console.warn('[user-profile] Failed to load photo for', userId, err);
    photoCache.set(userId, null);
    return null;
  }
}

/**
 * Batch-fetch photo URLs for a list of user IDs. Returns a map keyed by
 * userId. Parallelised; missing photos come back as null.
 */
export async function getUserPhotos(
  userIds: string[]
): Promise<Record<string, string | null>> {
  const unique = Array.from(new Set(userIds.filter(Boolean)));
  const entries = await Promise.all(
    unique.map(async (id) => [id, await getUserPhotoUrl(id)] as const)
  );
  return Object.fromEntries(entries);
}

/**
 * Clear the cache — used rarely (e.g., after profile edit).
 */
export function clearPhotoCache(userId?: string) {
  if (userId) {
    photoCache.delete(userId);
  } else {
    photoCache.clear();
  }
}
